<template>
    <div class="d-flex flex-wrap flex-stack my-5">
        <router-link to="/module/source/group" class="btn btn-primary align-self-center">
            <span class="svg-icon svg-icon-2">
                <inline-svg src="/media/icons/duotune/arrows/arr021.svg" />
            </span>
            {{ $t("pages.module.source.group.title") }}
        </router-link>
    </div>
    <custom-table
        :title="sprintf($t('pages.module.source.category.titlePattern'), [group.name ?? ''])"
        :subTitle="$t('pages.module.source.category.subTitle')"
        expandableRows
        selectableRows
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow"
        @changeTable="handleTableChange">
        <template v-slot:title="{ row: record }">
            {{ resolveDatum(record.translations, $root.defaultLanguage.id, 'language_id').title }}
        </template>
        <template v-slot:status="{ row: record }">
            <span :class="record.active ? 'badge-light-success': 'badge-light-danger'" class="badge">{{ record.active ? $t('common.active') : $t('common.passive') }}</span>
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('pages.module.source.title')" placement="top">
                    <router-link :to="sprintf('/module/source/group/%d/category/%d/source', [groupID, record.id])" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/text/txt009.svg" />
                        </span>
                    </router-link>
                </el-tooltip>
                <a v-on:click="newCategory(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/arrows/arr075.svg" />
                    </span>
                </a>
                <a v-on:click="fetchCategory(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/art/art005.svg" />
                    </span>
                </a>
                <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteRecord([record.id])">
                    <template #reference>
                        <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                            </span>
                        </a>
                    </template>
                </el-popconfirm>
            </div>
        </template>
    </custom-table>
    <div class="modal fade" id="kt_modal_source_category" ref="sourceCategoryModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ form.title }}</h2>
                    <div id="kt_modal_add_customer_close" data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onSubmit()" :model="form" ref="sourceCategoryForm">
                    <div class="modal-body py-10 px-lg-17">
                        <div class="scroll-y me-n7 pe-7" id="kt_modal_add_customer_scroll" data-kt-scroll="true" data-kt-scroll-activate="{default: false, lg: true}" data-kt-scroll-max-height="auto" data-kt-scroll-dependencies="#kt_modal_add_customer_header" data-kt-scroll-wrappers="#kt_modal_add_customer_scroll" data-kt-scroll-offset="300px">
                            <el-divider content-position="center">
                                {{ $t("pages.module.source.category.generalInformation") }}
                            </el-divider>

                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.module.source.category.cols.title') }}</label>
                                <el-form-item prop="translate.title" :rules="$validation.getMessage(['required'])">
                                    <el-input v-model="form.translate.title" type="text"/>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.source.category.cols.description') }}</label>
                                <el-form-item prop="translate.description">
                                    <el-input v-model="form.translate.description" type="textarea" rows="5"/>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('common.sort') }}</label>
                                <el-form-item prop="data.sort" :rules="$validation.getMessage(['required'])">
                                    <el-input-number v-model="form.data.sort" :min="1" />
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-10">
                                <label class="fs-6 fw-bold mb-2">{{ $t('common.status') }}</label>
                                <el-form-item prop="data.active">
                                    <el-radio-group v-model="form.data.active">
                                        <el-radio-button :label="true">{{ $t("common.active") }}</el-radio-button>
                                        <el-radio-button :label="false">{{ $t("common.passive") }}</el-radio-button>
                                    </el-radio-group>
                                </el-form-item>
                            </div>

                            <el-divider content-position="center">
                                {{ $t("pages.module.source.category.seoSettings") }}
                            </el-divider>

                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.source.category.seoCols.slug') }}</label>
                                <el-form-item prop="translate.meta.slug">
                                    <el-input v-model="form.translate.slug" type="text"/>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.source.category.seoCols.title') }}</label>
                                <el-form-item prop="translate.meta.title">
                                    <el-input v-model="form.translate.meta.title" type="text"/>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.source.category.seoCols.description') }}</label>
                                <el-form-item prop="translate.meta.description">
                                    <el-input v-model="form.translate.meta.description" type="textarea" rows="3"/>
                                </el-form-item>
                            </div>
                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.module.source.category.seoCols.keywords') }}</label>
                                <el-form-item prop="translate.meta.keyword">
                                    <el-input v-model="form.translate.meta.keyword" type="textarea" rows="3"/>
                                </el-form-item>
                            </div>

                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                            <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="form.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "index",
    components: {
        CustomTable
    },
    data(){
        return {
            table: {
                loading: false,
                data: []
            },
            fields: [
                {
                    name: this.$t("common.id"),
                    key: "id",
                    class: ""
                },
                {
                    name: this.$t("pages.module.source.category.cols.title"),
                    scopedSlots: {customRender: "title"}
                },
                {
                    name: this.$t("common.sort"),
                    key: "sort",
                    class: ""
                },
                {
                    name: this.$t("common.status"),
                    scopedSlots: {customRender: "status"}
                },
                {
                    name: this.$t("common.action"),
                    key: "action",
                    scopedSlots: {customRender: "actions"}
                }
            ],
            actions: [
                {
                    name: "new",
                    icon: "bi-plus-lg",
                    label: "btn.add"
                },
                {
                    name: "refresh",
                    icon: "bi-arrow-repeat",
                    label: "btn.refresh"
                },
                {
                    name: "delete",
                    icon: "bi-trash",
                    label: "btn.delete"
                }
            ],
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                translate: {
                    meta: {}
                },
                data: {}
            },
            group: {},
            selectedRowKeys: []
        }
    },
    computed: {
        groupID() {
            return this.$route.params.id;
        },
    },
    created() {
        if (!this.groupID || !(this.groupID > 0)) {
            this.$router.push({
                path: "/module/source/group"
            });
        }
    },
    mounted(){
        this.setCurrentPageBreadcrumbs(this.$t("pages.module.source.category.title"), [this.$t("menu.moduleManagement")]);

        if (this.groupID && this.groupID > 0) {
            this.loadTable();
            this.loadGroup();
        }
    },
    methods: {
        loadGroup(){
            this.axios.get(this.endpoints['module_source_group'] + '/' + this.groupID).then((response) => {
                let data = response.data.data;
                this.group = data;
             }).catch((error) => {
                if(error.response.data.message == 'record_not_found') {
                    this.$router.push({
                        path: "/module/source/group"
                    });
                }
            });
        },
        loadTable(){
            this.table.loading = true;

            this.axios.get(this.endpoints['module_source_category'] + '/tree', {
                params: {
                    group_id: this.groupID
                }
            }).then((response) => {
                this.table.data = response.data.data;
            }).finally(() => {
                this.table.loading = false;
            });
        },
        newCategory(record = null){
            this.form.updateStatus = false;
            this.form.translate = {
                language_id: this.$root.defaultLanguage.id,
                meta: {}
            };
            this.form.data = {
                group_id: this.groupID,
                sort: 1,
                active: true
            };
            this.form.title = this.$t("pages.module.source.category.newCategory");

            if(record) {
                let categoryTranslate = this.resolveDatum(record.translations, this.$root.defaultLanguage.id, 'language_id');

                this.form.title = this.sprintf(this.$t("pages.module.source.category.newCategoryWithParent"), [categoryTranslate.title]);
                this.form.data.parent_id = record.id;
            }

            this.showModal(this.$refs.sourceCategoryModal);
        },
        fetchCategory(record){
            this.form.updateStatus = true;
            this.axios.get(this.endpoints['module_source_category']+ '/' + record.id).then(response => {
                let data = response.data.data;

                let translate = this.resolveDatum(data.translations, this.$root.defaultLanguage.id, 'language_id');
                let translateLinks = this.resolveDatum(data.links, this.$root.defaultLanguage.id, 'language_id');

                this.form.title =  this.sprintf(this.$t("pages.module.source.category.editCategory"), [translate.title]);
                this.form.translate = Object.assign(translate, {
                    meta: {
                        title: translateLinks.title,
                        description: translateLinks.description,
                        keyword: translateLinks.keyword
                    }
                });
                this.form.data = response.data.data;

                this.showModal(this.$refs.sourceCategoryModal);
            });
        },
        onSubmit(){
            this.$refs.sourceCategoryForm.validate((valid) => {
                if(valid) {
                    let formData = this.prepareFormData();
                    this.form.loading = true;

                    if(this.form.data.id) {
                        this.axios.put(this.endpoints['module_source_category'] + '/' + this.form.data.id, formData).then(response => {
                            this.onResponse(response.data, () => {
                                this.loadTable();
                                this.hideModal(this.$refs.sourceCategoryModal);
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }else {
                        this.axios.post(this.endpoints['module_source_category'], formData).then(response => {
                            this.onResponse(response.data, () => {
                                this.loadTable();
                                this.hideModal(this.$refs.sourceCategoryModal);
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }
                } else {
                    return false;
                }
            });
        },
        prepareFormData(){
            let formData = this.form.data;

            return {...this.form.translate, ...formData};
        },
        deleteRecord(id){
            this.$store.dispatch("module/source/category/delete", {
                id: id,
                refreshData: false
            }).then((successDeleted) => {
                this.selectedRowKeys = this.selectedRowKeys.filter(x => !successDeleted.includes(x));
                if(successDeleted) {
                    this.loadTable();
                }
            });
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.loadTable();
                    break;

                case "new":
                    this.newCategory();
                    break;

                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;

                default:
                    break;
            }
        },
        handleTableChange(pagination, filterData) {
            this.$store.dispatch("module/source/category/get", {
                page: pagination,
                filterData: Object.assign({
                    group_id: this.groupID
                }, filterData)
            });
        },
        handleSelectedRow(record){
            this.selectedRowKeys = record;
        }
    }
}
</script>

<style>
</style>